import { useState, useRef, useEffect, useMemo } from 'react'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts'
import { LineChartIcon, BarChartIcon, ExpandIcon, ContractIcon } from './Icons'
import { CustomTooltip, CustomBarTooltip, CustomStackedBarTooltip } from './Tooltips'
import { formatNumber } from '../../../utils'
import DataSourceSelect from './components/DataSourceSelect'
import CustomDot from './components/CustomDot'
import CustomTick from './components/CustomTick'
// TODO: SPLIT THIS INTO MULTIPLE FILES
// THIS FILE IS TOOOO LONG
// TOO MANY PROPS BEING PASSED AROUND, These can be handled in stores and used via dependency injection

const TrendsChartSection = ({
  chartType,
  setChartType,
  singlePolygonData,
  singlePolygonId,
  polygonTrendsData,
  populationTrendsData,
  showPolygonLines,
  showIndividualPolygonLines,
  polygonData,
  selectedLine,
  targetGroupId,
  targetGroupName,
  aggSelector,
  polygonColors,
  rightSidebarStore,
  populationDataset = 'landscan',
  worldpopAvailable = false,
  worldpopLoading = true,
  safeTargetGroupName,
  uiStore,
  isExpanded,
  onToggleExpand
}) => {
  const [highlightedYear, setHighlightedYear] = useState(null)
  const [hoveredStackedKey, setHoveredStackedKey] = useState(null)
  const [hoveredLineKey, setHoveredLineKey] = useState(null)
  const [hiddenPolygonIds, setHiddenPolygonIds] = useState([])
  const [chartTooltipPosition, setChartTooltipPosition] = useState(null)
  const hoveredPolygonIdRef = useRef(null)
  const tooltipPointerInsideRef = useRef(false)
  const [tooltipPointerInside, setTooltipPointerInside] = useState(false)
  const [tooltipActive, setTooltipActive] = useState(false)

  const lineSeriesDataKey = useMemo(() => {
    if (singlePolygonData && singlePolygonData.stats && singlePolygonData.stats.total_population) {
      return 'population'
    }
    if (showPolygonLines && polygonTrendsData.length > 0) {
      if (showIndividualPolygonLines) return null
      return aggSelector === 'avg' ? 'average_population' : 'aggregate_population'
    }
    return 'population'
  }, [
    singlePolygonData,
    showPolygonLines,
    polygonTrendsData.length,
    showIndividualPolygonLines,
    aggSelector
  ])

  useEffect(() => {
    setHighlightedYear(null)
  }, [singlePolygonId, targetGroupId])

  useEffect(() => {
    // Reset hidden state when chart context changes.
    setHiddenPolygonIds([])
  }, [targetGroupId, singlePolygonId, showPolygonLines, showIndividualPolygonLines])

  useEffect(() => {
    if (!rightSidebarStore) return
    const syncedYear = rightSidebarStore.populationSelectedYear
    setHighlightedYear(syncedYear ? syncedYear.toString() : null)
  }, [rightSidebarStore, rightSidebarStore && rightSidebarStore.populationSelectedYear])

  const handleYearClick = (yearValue) => {
    const year = yearValue ? yearValue.toString() : null
    setHighlightedYear(year)
    if (rightSidebarStore && year) {
      rightSidebarStore.setPopulationSelectedYear(year)
    }
  }
  // should be in a seperate file that contains constants
  const TOOLTIP_WIDTH = 157
  const TOOLTIP_BELOW_OFFSET = 12
  const containerRef = useRef(null)

  // this should be a util method
  const getTooltipPositionBelowCursor = (x, y) => {
    let tooltipX = Math.round(x - TOOLTIP_WIDTH / 2)
    const tooltipY = Math.round(y + TOOLTIP_BELOW_OFFSET)

    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth
      if (tooltipX + TOOLTIP_WIDTH > containerWidth) {
        tooltipX = containerWidth - TOOLTIP_WIDTH
      }
      if (tooltipX < 0) {
        tooltipX = 0
      }
    }

    return { x: tooltipX, y: tooltipY }
  }

  const handleDotHover = (_pointPayload, lineKey, dotMeta) => {
    console.error('HOVERING OVER DOT')
    setHoveredLineKey(lineKey || null)
    setTooltipActive(true)
    if (
      dotMeta &&
      typeof dotMeta.cx === 'number' &&
      typeof dotMeta.cy === 'number'
    ) {
      setChartTooltipPosition(getTooltipPositionBelowCursor(dotMeta.cx, dotMeta.cy))
    }
  }

  // const handleDotLeave = (e) => {
  //   console.log(e)
  //   setTooltipActive(false)
  // }

  const isTooltipElement = (node) => {
    if (!node || typeof node.closest !== 'function') return false
    return !!node.closest('.people-data-sidebar__tooltip-container')
  }

  const handleLineLeave = () => {
    setHoveredLineKey(null)
    setChartTooltipPosition(null)
    if (rightSidebarStore) {
      const pinned = rightSidebarStore.populationSelectedYear
      setHighlightedYear(pinned != null && pinned !== '' ? pinned.toString() : null)
    }
  }

  const handleTooltipPointerEnter = () => {
    setTooltipActive(true)
    tooltipPointerInsideRef.current = true
    setTooltipPointerInside(true)
  }

  const handleTooltipPointerLeave = () => {
    tooltipPointerInsideRef.current = false
    setTooltipPointerInside(false)
    setTooltipActive(false)
    handleLineLeave()
  }

  const handleLineOrChartMouseLeave = (event) => {
    const nextTarget = event && event.relatedTarget
    if (isTooltipElement(nextTarget)) {
      handleTooltipPointerEnter()
      return
    }
    if (!tooltipPointerInsideRef.current) {
      handleLineLeave()
    }
  }

  const handleLineChartMouseMove = (chartState) => {
    if (!chartState || !chartState.activeCoordinate) return
    if (tooltipPointerInsideRef.current) return
    const { x, y } = chartState.activeCoordinate
    if (typeof x !== 'number' || typeof y !== 'number') return
    setChartTooltipPosition(getTooltipPositionBelowCursor(x, y))
  }

  const getClickedYearFromChartEvent = (dataArg) => {
    if (!dataArg) return null
    const candidateYear =
      dataArg.activeLabel ||
      (dataArg.activePayload && dataArg.activePayload[0] && dataArg.activePayload[0].payload && dataArg.activePayload[0].payload.year) ||
      (dataArg.payload && dataArg.payload.year) ||
      (dataArg.year)
    return candidateYear !== null && candidateYear !== undefined ? candidateYear.toString() : null
  }

  const handleIndividualShapeDotClick = (polygonId, yearValue) => {
    if (!rightSidebarStore || !polygonId) return
    const clickedYear = yearValue != null ? yearValue.toString() : null
    rightSidebarStore.setSelectedLine(
      polygonId,
      clickedYear || rightSidebarStore.populationSelectedYear || rightSidebarStore.latestYear
    )
  }

  const visiblePolygonData = polygonData.filter(polygon => !hiddenPolygonIds.includes(polygon.id))
  const lineChartData =
    // Priority: single polygon data > polygon lines > target group data
    singlePolygonData && singlePolygonData.stats && singlePolygonData.stats.total_population
      ? singlePolygonData.stats.total_population.map(item => ({
        year: item.year,
        population: item.value || 0
      }))
      : (showPolygonLines && polygonTrendsData.length > 0 ? polygonTrendsData : populationTrendsData)
  const targetGroupBarData = singlePolygonData && singlePolygonData.stats && singlePolygonData.stats.total_population
    ? singlePolygonData.stats.total_population.map(item => ({
      year: item.year,
      population: item.value || 0
    }))
    : (populationTrendsData || [])

  const shouldRotateYearLabels = (lineChartData && lineChartData.length > 9)
  const yearTickAngle = shouldRotateYearLabels ? -45 : 0
  const xAxisHeight = shouldRotateYearLabels ? 38 : 30
  const expandedChartHeight = isExpanded
    ? '100%'
    : (chartType === 'bar'
        ? Math.max(200, (targetGroupBarData.length || 0) * 30 + 10)
        : 180)

  const togglePolygonVisibility = (polygonId) => {
    setHiddenPolygonIds(prev =>
      prev.includes(polygonId)
        ? prev.filter(id => id !== polygonId)
        : [...prev, polygonId]
    )
  }

  return (
    <div className={`people-data-sidebar__section people-data-sidebar__section--trends ${isExpanded ? 'people-data-sidebar__section--expanded-chart' : ''}`}>
      <div className='people-data-sidebar__section-header people-data-sidebar__section-header--trends'>
        <div className='people-data-sidebar__trends-header-top'>
          <h3 className='people-data-sidebar__section-title'>Population Trends</h3>
          <button
            type='button'
            className='people-data-sidebar__expand-btn people-data-sidebar__expand-btn--header-right'
            onClick={onToggleExpand}
            aria-label={isExpanded ? 'Contract population trends chart' : 'Expand population trends chart'}
          >
            {isExpanded ? <ContractIcon /> : <ExpandIcon />}
          </button>
        </div>
        <div className='people-data-sidebar__trends-header-bottom'>
          <div className='people-data-sidebar__trends-dataset-controls'>
            <div className='people-data-sidebar__dropdown people-data-sidebar__dropdown--trends'>
              <DataSourceSelect />
            </div>
          </div>
          <div className='people-data-sidebar__chart-toggle'>
            <button
              type='button'
              className={`people-data-sidebar__chart-toggle-btn ${chartType === 'line' ? 'active' : ''}`}
              onClick={() => setChartType('line')}
            >
              <LineChartIcon active={chartType === 'line'} />
            </button>
            <button
              type='button'
              className={`people-data-sidebar__chart-toggle-btn ${chartType === 'bar' ? 'active' : ''}`}
              onClick={() => setChartType('bar')}
            >
              <BarChartIcon active={chartType === 'bar'} />
            </button>
          </div>
        </div>
      </div>
      <div
        className='people-data-sidebar__chart-container'
        ref={containerRef}
        onMouseLeave={(e) => {
          if (chartType === 'line') {
            handleLineOrChartMouseLeave(e)
          }
        }}
        onClick={(e) => {
          // Only handle clicks on chart background (not on lines, circles, or text elements like year labels)
          // Check if click is on chart canvas area (not on SVG elements)
          // Only deselect line if one is selected, but don't show individual shapes on background click
          if (targetGroupId && chartType === 'line' && e.target.tagName !== 'path' && e.target.tagName !== 'circle' && e.target.tagName !== 'text' && e.target.tagName !== 'g') {
            // If clicking on chart background, only deselect line if one is selected
            if (selectedLine) {
              rightSidebarStore.clearSelectedLine()
            }
            // Removed: Don't show individual shapes on chart background click - user must click "See Individual Shapes" button
          }
        }}
      >
        <ResponsiveContainer
          width='100%'
          height={expandedChartHeight}
          style={{
            minHeight: chartType === 'bar'
              ? (isExpanded ? '100%' : Math.max(200, (targetGroupBarData.length || 0) * 30 + 10))
              : (isExpanded ? '100%' : 180)
          }}
        >
          {chartType === 'line'
            ? (
              <LineChart
                data={
                  // Priority: single polygon data > polygon lines > target group data
                  singlePolygonData && singlePolygonData.stats && singlePolygonData.stats.total_population
                    ? singlePolygonData.stats.total_population.map(item => ({
                      year: item.year,
                      population: item.value || 0
                    }))
                    : (
                        showPolygonLines && polygonTrendsData.length > 0
                          ? polygonTrendsData
                          : populationTrendsData
                      )
                }
                margin={{ top: 18, right: 18, left: 6, bottom: -2 }}
                onMouseMove={handleLineChartMouseMove}
                activeIndex={singlePolygonData && singlePolygonData.stats && singlePolygonData.stats.total_population
                  ? singlePolygonData.stats.total_population.length - 1
                  : null}
              >
                <CartesianGrid strokeDasharray='3 3' stroke='#E5E7EB' style={{ pointerEvents: 'none' }} />
                <XAxis
                  dataKey='year'
                  stroke='#475569'
                  fontSize={11.5}
                  tickLine={false}
                  interval={0}
                  height={xAxisHeight}
                  tick={(props) => (
                    <CustomTick
                      x={props.x}
                      y={props.y}
                      payload={props.payload}
                      highlightedYear={highlightedYear}
                      onYearClick={handleYearClick}
                      tickAngle={yearTickAngle}
                    />
                  )}
                />
                <YAxis
                  stroke='#475569'
                  domain={[dataMin => dataMin / 2, dataMax => (dataMax * 1.1)]}
                  fontSize={11.5}
                  tick={{ fill: '#334155', fontSize: 11.5, fontWeight: 500 }}
                  tickLine={false}
                  width={54}
                  tickMargin={6}
                  tickFormatter={(value) => {
                    if (value >= 1000) return formatNumber(value)
                    return value
                  }}
                />
                {tooltipActive && (
                <Tooltip
                  content={
                    <CustomTooltip
                      active={tooltipActive}
                      showPolygonLines={showPolygonLines}
                      singlePolygonData={singlePolygonData}
                      showIndividualPolygonLines={showIndividualPolygonLines}
                      polygonData={polygonData}
                      polygonColors={polygonColors}
                      populationTrendsData={populationTrendsData}
                      targetGroupName={targetGroupName}
                      targetGroupId={targetGroupId}
                      uiStore={uiStore}
                      singlePolygonId={singlePolygonId}
                      hoveredPolygonIdRef={hoveredPolygonIdRef}
                      hoveredLineKey={hoveredLineKey}
                      lineSeriesDataKey={lineSeriesDataKey}
                      tooltipPointerInside={tooltipPointerInside}
                      restrictTooltipToLineHover
                      rightSidebarStore={rightSidebarStore}
                      onTooltipEnter={handleTooltipPointerEnter}
                      onTooltipLeave={handleTooltipPointerLeave}
                    />
                }
                  isAnimationActive={false}
                  active
                  allowEscapeViewBox={{ x: true, y: true }}
                  cursor={false}
                  wrapperStyle={{ pointerEvents: 'auto' }}
                  position={chartTooltipPosition || undefined}
                />
                )}
                {singlePolygonData && singlePolygonData.stats && singlePolygonData.stats.total_population
                  ? (
                // Show single polygon line when a shape is clicked from left sidebar
                      [
                        <Line
                          key='single-hit'
                          type='monotone'
                          dataKey='population'
                          stroke='transparent'
                          fill='none'
                          strokeWidth={20}
                          dot={false}
                          activeDot={false}
                          name={targetGroupName || 'Polygon'}
                          onMouseEnter={() => {
                            setHoveredLineKey('population')
                            setTooltipActive(false)
                            // Highlight the single polygon on the map when hovering over its line
                            if (uiStore && singlePolygonId) {
                              uiStore.highlightPolygonFromChart(singlePolygonId, '#648E62')
                            }
                          }}
                          onMouseLeave={() => {
                            handleLineLeave()
                            // Unhighlight the single polygon when mouse leaves
                            if (uiStore && singlePolygonId) {
                              uiStore.unhighlightPolygonFromChart(singlePolygonId)
                            }
                          }}
                          style={{ cursor: 'default' }}
                        />,
                        <Line
                          key='single-visual'
                          type='monotone'
                          dataKey='population'
                          stroke='#648E62'
                          strokeWidth={2}
                          style={{ cursor: 'default' }}
                          onMouseEnter={() => {
                            setTooltipActive(false)
                            setHoveredLineKey('population')
                          }}
                          onMouseLeave={(e) => handleLineOrChartMouseLeave(e)}
                          dot={(props) => (
                            <CustomDot
                              {...props}
                              highlightedYear={highlightedYear}
                              lineKey='population'
                              onDotHover={(pointPayload, lineKey, dotMeta) => handleDotHover(pointPayload, lineKey, dotMeta)}
                          // onDotLeave={handleDotLeave}
                              onYearClick={handleYearClick}
                            />
                          )}
                          activeDot={false}
                          isAnimationActive={false}
                        />
                      ]
                    )
                  : showPolygonLines && polygonTrendsData.length > 0
                    ? (
                      // When showing polygon lines, check if we should show individual lines or grouped line
                      // If showIndividualPolygonLines is true, show individual lines for each polygon
                        showIndividualPolygonLines
                          ? (
                              [
                                /* All hit lines first (below), then all visuals so dots are never covered by another series' hit stroke */
                                ...visiblePolygonData.map((polygon, index) => {
                                  const dataKey = `polygon_${polygon.id}`
                                  const color = polygonColors[index % polygonColors.length]
                                  return (
                                    <Line
                                      key={`hit-${polygon.id}`}
                                      type='monotone'
                                      dataKey={dataKey}
                                      stroke='transparent'
                                      fill='none'
                                      strokeWidth={20}
                                      dot={false}
                                      activeDot={false}
                                      name={polygon.name || `Polygon ${index + 1}`}
                                      onClick={(data, _index, e) => {
                                        if (e && typeof e.stopPropagation === 'function') {
                                          e.stopPropagation()
                                        }
                                        const clickedYear = getClickedYearFromChartEvent(data)
                                        rightSidebarStore.setSelectedLine(
                                          polygon.id,
                                          clickedYear || rightSidebarStore.populationSelectedYear || rightSidebarStore.latestYear
                                        )
                                      }}
                                      onMouseEnter={() => {
                                        setHoveredLineKey(dataKey)
                                        if (uiStore && polygon.id) {
                                          uiStore.highlightPolygonFromChart(polygon.id, color)
                                        }
                                      }}
                                      onMouseLeave={() => {
                                        handleLineLeave()
                                        if (uiStore && polygon.id) {
                                          uiStore.unhighlightPolygonFromChart(polygon.id)
                                        }
                                      }}
                                      style={{ cursor: 'pointer' }}
                                    />
                                  )
                                }),
                                ...visiblePolygonData.map((polygon, index) => {
                                  const dataKey = `polygon_${polygon.id}`
                                  const color = polygonColors[index % polygonColors.length]
                                  const isSelected = selectedLine === polygon.id
                                  return (
                                    <Line
                                      key={`visual-${polygon.id}`}
                                      type='monotone'
                                      dataKey={dataKey}
                                      stroke={color}
                                      strokeWidth={isSelected ? 4 : 2}
                                      style={{ cursor: 'pointer' }}
                                      onClick={(data, _index, e) => {
                                        if (e && typeof e.stopPropagation === 'function') {
                                          e.stopPropagation()
                                        }
                                        const clickedYear = getClickedYearFromChartEvent(data)
                                        rightSidebarStore.setSelectedLine(
                                          polygon.id,
                                          clickedYear || rightSidebarStore.populationSelectedYear || rightSidebarStore.latestYear
                                        )
                                      }}
                                      dot={(props) => (
                                        <CustomDot
                                          {...props}
                                          highlightedYear={highlightedYear}
                                          lineKey={dataKey}
                                          onDotHover={(pointPayload, lineKey, dotMeta) => handleDotHover(pointPayload, lineKey, dotMeta)}
                                          onYearClick={handleYearClick}
                                          onDotClick={(pointPayload) => handleIndividualShapeDotClick(polygon.id, pointPayload && pointPayload.year)}
                                          restrictHighlightToSelectedLine
                                          isLineSelected={isSelected}
                                        />
                                      )}
                                      activeDot={false}
                                      isAnimationActive={false}
                                    />
                                  )
                                })
                              ]
                            )
                          : (
                        // Show grouped line based on selector
                              aggSelector === 'avg'
                                ? (
                              // Show single average line for all polygons
                                    [
                                      <Line
                                        key='avg-hit'
                                        type='monotone'
                                        dataKey='average_population'
                                        stroke='transparent'
                                        fill='none'
                                        strokeWidth={20}
                                        dot={false}
                                        activeDot={false}
                                        name={targetGroupName || 'Average'}
                                        onClick={(_data, _index, e) => {
                                          if (e && typeof e.stopPropagation === 'function') {
                                            e.stopPropagation()
                                          }
                                          // Don't trigger if single polygon data is showing
                                          if (!singlePolygonData) {
                                            rightSidebarStore.showShapeLines()
                                          }
                                        }}
                                        onMouseEnter={() => { setHoveredLineKey('average_population'); setTooltipActive(false) }}
                                        onMouseLeave={(e) => handleLineOrChartMouseLeave(e)}
                                        style={{ cursor: 'pointer' }}
                                      />,
                                      <Line
                                        key='avg-visual'
                                        type='monotone'
                                        dataKey='average_population'
                                        stroke='#648E62'
                                        strokeWidth={2}
                                        style={{ cursor: 'pointer' }}
                                        onClick={(_data, _index, e) => {
                                          if (e && typeof e.stopPropagation === 'function') {
                                            e.stopPropagation()
                                          }
                                          if (!singlePolygonData) {
                                            rightSidebarStore.showShapeLines()
                                          }
                                        }}
                                        onMouseEnter={() => { setHoveredLineKey('average_population'); setTooltipActive(false) }}
                                        onMouseLeave={(e) => handleLineOrChartMouseLeave(e)}
                                        dot={(props) => (
                                          <CustomDot
                                            {...props}
                                            highlightedYear={highlightedYear}
                                            lineKey='average_population'
                                            onDotHover={(pointPayload, lineKey, dotMeta) => handleDotHover(pointPayload, lineKey, dotMeta)}
                                            onYearClick={handleYearClick}
                                          />
                                        )}
                                        activeDot={false}
                                        isAnimationActive={false}
                                      />
                                    ]
                                  )
                                : aggSelector === 'dissolved'
                                  ? (
                                      [
                                        <Line
                                          key='dissolved-hit'
                                          type='monotone'
                                          dataKey='aggregate_population'
                                          stroke='transparent'
                                          fill='none'
                                          strokeWidth={20}
                                          dot={false}
                                          activeDot={false}
                                          name={targetGroupName || 'Dissolved'}
                                          onClick={(data, index, e) => {
                                            if (e && typeof e.stopPropagation === 'function') {
                                              e.stopPropagation()
                                            }
                                            // Don't trigger if single polygon data is showing
                                            if (!singlePolygonData) {
                                              rightSidebarStore.showShapeLines()
                                            }
                                          }}
                                          onMouseEnter={() => { setHoveredLineKey('aggregate_population'); setTooltipActive(false) }}
                                          onMouseLeave={(e) => handleLineOrChartMouseLeave(e)}
                                          style={{ cursor: 'pointer' }}
                                        />,
                                        <Line
                                          key='dissolved-visual'
                                          type='monotone'
                                          dataKey='aggregate_population'
                                          stroke='#648E62'
                                          strokeWidth={2}
                                          style={{ cursor: 'pointer' }}
                                          onClick={(data, index, e) => {
                                            if (e && typeof e.stopPropagation === 'function') {
                                              e.stopPropagation()
                                            }
                                            if (!singlePolygonData) {
                                              rightSidebarStore.showShapeLines()
                                            }
                                          }}
                                          onMouseEnter={() => setHoveredLineKey('aggregate_population')}
                                          onMouseLeave={(e) => handleLineOrChartMouseLeave(e)}
                                          dot={(props) => (
                                            <CustomDot
                                              {...props}
                                              highlightedYear={highlightedYear}
                                              lineKey='aggregate_population'
                                              onDotHover={(pointPayload, lineKey, dotMeta) => handleDotHover(pointPayload, lineKey, dotMeta)}
                                              onYearClick={handleYearClick}
                                            />
                                          )}
                                          isAnimationActive={false}
                                        />
                                      ]
                                    )
                                  : (
                                // Show single aggregate (sum) line for all polygons
                                      [
                                        <Line
                                          key='agg-hit'
                                          type='monotone'
                                          dataKey='aggregate_population'
                                          stroke='transparent'
                                          fill='none'
                                          strokeWidth={20}
                                          dot={false}
                                          activeDot={false}
                                          name={targetGroupName || 'Aggregate'}
                                          onClick={(data, index, e) => {
                                            if (e && typeof e.stopPropagation === 'function') {
                                              e.stopPropagation()
                                            }
                                            // Don't trigger if single polygon data is showing
                                            if (!singlePolygonData) {
                                              rightSidebarStore.showShapeLines()
                                            }
                                          }}
                                          onMouseEnter={() => { setHoveredLineKey('aggregate_population'); setTooltipActive(false) }}
                                          onMouseLeave={(e) => handleLineOrChartMouseLeave(e)}
                                          style={{ cursor: 'pointer' }}
                                        />,
                                        <Line
                                          key='agg-visual'
                                          type='monotone'
                                          dataKey='aggregate_population'
                                          stroke='#648E62'
                                          strokeWidth={2}
                                          style={{ cursor: 'pointer' }}
                                          onClick={(data, index, e) => {
                                            if (e && typeof e.stopPropagation === 'function') {
                                              e.stopPropagation()
                                            }
                                            if (!singlePolygonData) {
                                              rightSidebarStore.showShapeLines()
                                            }
                                          }}
                                          onMouseEnter={() => setHoveredLineKey('aggregate_population')}
                                          onMouseLeave={(e) => handleLineOrChartMouseLeave(e)}
                                          dot={(props) => (
                                            <CustomDot
                                              {...props}
                                              highlightedYear={highlightedYear}
                                              lineKey='aggregate_population'
                                              onDotHover={(pointPayload, lineKey, dotMeta) => handleDotHover(pointPayload, lineKey, dotMeta)}
                                              onYearClick={handleYearClick}
                                            />
                                          )}
                                          isAnimationActive={false}
                                        />
                                      ]
                                    )
                            )
                      )
                    : (
                        [
                          <Line
                            key='default-hit'
                            type='monotone'
                            dataKey='population'
                            stroke='transparent'
                            fill='none'
                            strokeWidth={20}
                            dot={false}
                            activeDot={false}
                            onClick={(data, index, e) => {
                              if (e && typeof e.stopPropagation === 'function') {
                                e.stopPropagation()
                              }
                              // When clicking on main line, show polygon lines (only if no single polygon data)
                              if (!singlePolygonData) {
                                rightSidebarStore.showShapeLines()
                              }
                            }}
                            onMouseEnter={() => { setHoveredLineKey('population'); setTooltipActive(false) }}
                            onMouseLeave={(e) => handleLineOrChartMouseLeave(e)}
                            style={{ cursor: 'pointer' }}
                          />,
                          <Line
                            key='default-visual'
                            type='monotone'
                            dataKey='population'
                            stroke='#648E62'
                            strokeWidth={2}
                            style={{ cursor: 'pointer' }}
                            onClick={(data, index, e) => {
                              if (e && typeof e.stopPropagation === 'function') {
                                e.stopPropagation()
                              }
                              if (!singlePolygonData) {
                                rightSidebarStore.showShapeLines()
                              }
                            }}
                            onMouseEnter={() => { setHoveredLineKey('population'); setTooltipActive(false) }}
                            onMouseLeave={(e) => handleLineOrChartMouseLeave(e)}
                            dot={(props) => (
                              <CustomDot
                                {...props}
                                highlightedYear={highlightedYear}
                                lineKey='population'
                                onDotHover={(pointPayload, lineKey, dotMeta) => handleDotHover(pointPayload, lineKey, dotMeta)}
                                onDotLeave={() => { setTooltipActive(false) }}
                                onYearClick={handleYearClick}
                              />
                            )}
                            activeDot={false}
                            isAnimationActive={false}
                          />
                        ]
                      )}
              </LineChart>
              )
            : (
          // Show individual shapes in bar chart when drill-down is active (but not when single polygon is showing)
                !singlePolygonData && showPolygonLines && showIndividualPolygonLines && polygonTrendsData.length > 0
                  ? (
                    <BarChart
                      layout='vertical'
                      data={polygonTrendsData.map(item => {
                      // Calculate sum for each year
                        const sum = visiblePolygonData.reduce((acc, polygon) => {
                          const value = item[`polygon_${polygon.id}`] || 0
                          return acc + value
                        }, 0)
                        return {
                          ...item,
                          sum
                        }
                      })}
                      margin={{ top: 10, right: 20, left: 10, bottom: 20 }}
                      barCategoryGap='15%'
                    >
                      <CartesianGrid strokeDasharray='3 3' stroke='#E5E7EB' />
                      <XAxis hide type='number' />
                      <YAxis
                        yAxisId={0}
                        axisLine={false}
                        tickLine={false}
                        orientation='left'
                        type='category'
                        dataKey='year'
                        stroke='#475569'
                        tick={{ fill: '#334155', fontSize: 11.5, fontWeight: 500 }}
                        width={42}
                        fontSize={11.5}
                        interval={0}
                      />
                      <YAxis
                        yAxisId={1}
                        axisLine={false}
                        tickLine={false}
                        orientation='right'
                        type='category'
                        dataKey='sum'
                        stroke='#475569'
                        tick={{ fill: '#334155', fontSize: 11.5, fontWeight: 500 }}
                        fontSize={11.5}
                        width={72}
                        tickFormatter={(value) => {
                          if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`
                          if (value >= 1000) return `${(value / 1000).toFixed(1)}K`
                          return value
                        }}
                      />
                      <Tooltip
                        content={<CustomStackedBarTooltip hoveredStackedKey={hoveredStackedKey} />}
                        cursor={{ fill: 'none' }}
                        isAnimationActive={false}
                      />
                      {visiblePolygonData.map((polygon, index) => {
                        const color = polygonColors[index % polygonColors.length]
                        const dataKey = `polygon_${polygon.id}`
                        return (
                          <Bar
                            key={`bar-${polygon.id}`}
                            stackId='year'
                            dataKey={dataKey}
                            fill={color}
                            name={polygon.name || `Polygon ${index + 1}`}
                            onMouseEnter={() => setHoveredStackedKey(dataKey)}
                            onMouseLeave={() => setHoveredStackedKey(null)}
                          />
                        )
                      })}
                    </BarChart>
                    )
                  : (
                    // Show target group bar chart
                    <BarChart
                      layout='vertical'
                      data={targetGroupBarData}
                      margin={{ top: 2, right: 20, left: 10, bottom: 20 }}
                      barCategoryGap='15%'
                    >
                      <XAxis hide type='number' />
                      <YAxis
                        yAxisId={0}
                        dataKey='year'
                        type='category'
                        orientation='left'
                        axisLine={false}
                        tickLine={false}
                        stroke='#475569'
                        tick={{ fill: '#334155', fontSize: 11.5, fontWeight: 500 }}
                        width={52}
                        fontSize={11.5}
                        interval={0}
                      />
                      <YAxis
                        yAxisId={1}
                        type='category'
                        orientation='right'
                        axisLine={false}
                        tickLine={false}
                        dataKey='population'
                        tick={{ fill: '#334155', fontSize: 11.5, fontWeight: 500 }}
                        tickFormatter={(val) => {
                          if (val >= 1000) {
                            return `${(val / 1000).toFixed(1)}K`
                          }
                          return val.toString()
                        }}
                        stroke='#475569'
                        fontSize={11.5}
                        width={64}
                      />
                      <Tooltip
                        content={
                          <CustomBarTooltip
                            showPolygonLines={showPolygonLines}
                            singlePolygonData={singlePolygonData}
                            showIndividualPolygonLines={showIndividualPolygonLines}
                            populationTrendsData={populationTrendsData}
                          />
                        }
                        isAnimationActive={false}
                        allowEscapeViewBox={{ x: true, y: true }}
                        cursor={{ fill: 'none' }}
                      />
                      <Bar
                        dataKey='population'
                        fill='#648E62'
                        barSize={25}
                        radius={[0, 4, 4, 0]}
                        cursor='pointer'
                      >
                        {targetGroupBarData.map((_, index) => {
                          return (
                            <Cell
                              key={`cell-${index}`}
                              cursor='pointer'
                              fill='#648E62'
                              onClick={() => {
                                if (!singlePolygonData && aggSelector !== 'dissolved') {
                                  // Always show individual shapes when clicking target group bar (works for both aggregate and average)
                                  if (rightSidebarStore.polygonData && rightSidebarStore.polygonData.length > 0) {
                                    rightSidebarStore.showPolygonLines = true
                                    rightSidebarStore.showIndividualPolygonLines = true
                                    rightSidebarStore.showIncomePolygonData = true
                                  } else if (rightSidebarStore.targetGroupId) {
                                    rightSidebarStore.showIndividualPolygonLines = true
                                    rightSidebarStore.showIncomePolygonData = true
                                    rightSidebarStore.fetchDataForAllPolygons(rightSidebarStore.targetGroupId, false)
                                  }
                                }
                              }}
                            />
                          )
                        })}
                      </Bar>
                    </BarChart>
                    )
              )}
        </ResponsiveContainer>
      </div>
      <div className={`people-data-sidebar__chart-legend ${chartType === 'bar' ? 'people-data-sidebar__chart-legend--bar' : ''}`}>
        {singlePolygonData
          ? (
        // Show single polygon name when a shape is clicked from left sidebar
            <>
              <span className='people-data-sidebar__chart-legend-line'>—</span>
              <span className='people-data-sidebar__chart-legend-text'>{safeTargetGroupName}</span>
            </>
            )
          : showPolygonLines && showIndividualPolygonLines && polygonData.length > 0
            ? (
          // Show all polygon names only when showing individual polygon lines
                polygonData.map((polygon, index) => {
                  const color = polygonColors[index % polygonColors.length]
                  const isHidden = hiddenPolygonIds.includes(polygon.id)
                  return (
                    <button
                      key={polygon.id}
                      type='button'
                      className='people-data-sidebar__chart-legend-item'
                      onClick={() => togglePolygonVisibility(polygon.id)}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        opacity: isHidden ? 0.45 : 1
                      }}
                      aria-label={`${isHidden ? 'Show' : 'Hide'} ${polygon.name || `Polygon ${index + 1}`} series`}
                    >
                      <span
                        className='people-data-sidebar__chart-legend-line'
                        style={{
                          color,
                          textDecoration: isHidden ? 'line-through' : 'none'
                        }}
                      >
                        —
                      </span>
                      <span
                        className='people-data-sidebar__chart-legend-text'
                        style={{ textDecoration: isHidden ? 'line-through' : 'none' }}
                      >
                        {polygon.name || `Polygon ${index + 1}`}
                      </span>
                    </button>
                  )
                })
              )
            : (
          // Show target group name for target group data or when showing average/aggregate
              <>
                <span className='people-data-sidebar__chart-legend-line'>—</span>
                <span className='people-data-sidebar__chart-legend-text'>{safeTargetGroupName}</span>
              </>
              )}
      </div>
    </div>
  )
}

export default TrendsChartSection
